import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { getCurrentUser } from '../../../../utils/authSession'
import {
  createTimesheetFromJob,
  fetchTimesheetJobOptionsPage,
  getJobById,
  getTimesheetDetail,
  updateTimesheet,
  getJobTimesheetCount,
} from '../api/timesheetApi'
import { canAddTimesheet } from '../../job/jobHelpers'
import { appToast } from '../../../../components/AppToast'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { updateJobStatus } from '../../job/api/jobApi'


function toDateInput(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

function splitHours(value) {
  const numeric = Number(value || 0)
  if (!Number.isFinite(numeric) || numeric <= 0) return { hours: '0', minutes: '0' }
  const hours = Math.floor(numeric)
  const minutes = Math.round((numeric - hours) * 60)
  return { hours: String(hours), minutes: String(minutes) }
}

function toHoursLogged(hours, minutes) {
  const h = Number(hours || 0)
  const m = Number(minutes || 0)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0
  const total = h + m / 60
  return Number(total.toFixed(2))
}

export function useTimesheetEntryScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const editingId = searchParams.get('id')
  const returnUrl = location.state?.returnTo || '/nerve/timesheet/list'
  const preselectedJobId = searchParams.get('jobId')
  const mode = String(searchParams.get('mode') || '').toLowerCase()

  const savingRef = useRef(false)
  const isViewMode = mode === 'view'
  const isEditMode = Boolean(editingId)
  const currentUser = useMemo(() => getCurrentUser(), [])

  const [form, setForm] = useState({
    entryDate: toDateInput(new Date()),
    jobId: isEditMode ? '' : String(preselectedJobId || ''),
    activityDescription: '',
    hours: '0',
    minutes: '0',
    approvalStatus: 'PENDING',
  })

  const [selectedJob, setSelectedJob] = useState(null)
  const [mappedWorkName, setMappedWorkName] = useState('')
  const [resourceName, setResourceName] = useState(currentUser.name)
  const [isLoading, setIsLoading] = useState(isEditMode)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [canMarkCompleted, setCanMarkCompleted] = useState(false)
  const [timesheetId, setTimesheetId] = useState(null)
  const [isMarkingComplete, setIsMarkingComplete] = useState(false)
  const [completedJobWarningAction, setCompletedJobWarningAction] = useState(null)

  // ── Paginated job fetcher — same pattern as fetchTaskOptionsPage in jobApi.js ──
  const fetchJobOptions = useCallback(
    (search, page, pageSize) => fetchTimesheetJobOptionsPage(search, page, pageSize, currentUser.id),
    [currentUser.id],
  )

  useEffect(() => {
    let active = true

    async function loadDetailIfEditing() {
      if (!editingId) return
      const detail = await getTimesheetDetail(editingId)
      if (!active) return
      const split = splitHours(detail?.hoursLogged)
      setForm((prev) => ({
        ...prev,
        entryDate: toDateInput(detail?.entryDate),
        jobId: detail?.jobId ? String(detail.jobId) : '',
        activityDescription: detail?.activityDescription || '',
        hours: split.hours,
        minutes: split.minutes,
        approvalStatus: String(detail?.approvalStatus || 'PENDING').toUpperCase(),
      }))
      setMappedWorkName(detail?.jobName || detail?.taskName || '')
      setResourceName(detail?.userName || currentUser.name)
    }

    async function bootstrap() {
      setIsLoading(true)
      setError('')
      try {
        await loadDetailIfEditing()
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load timesheet form.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    bootstrap()
    return () => {
      active = false
    }
  }, [currentUser.name, editingId])

  useEffect(() => {
    let active = true
    async function loadSelectedJob() {
      if (!form.jobId) {
        setSelectedJob(null)
        setCanMarkCompleted(false)
        return
      }
      try {
        const detail = await getJobById(form.jobId)
        if (!active) return

        // ── Guard: block creation against completed/closed jobs ──
        if (!isEditMode && !canAddTimesheet(detail?.status)) {
          appToast.error('Job already completed. No further timesheet accepted.')
          setForm((prev) => ({ ...prev, jobId: '' }))
          setSelectedJob(null)
          setCanMarkCompleted(false)
          return
        }

        setSelectedJob(detail)
        if (!mappedWorkName) {
          setMappedWorkName(detail?.job_name || detail?.job_code || '')
        }
        // Prefill estimated hours on NEW timesheet creation only
        if (!isEditMode) {
          const prefilled = splitHours(detail?.planned_hours)
          setForm((prev) => ({ ...prev, hours: prefilled.hours, minutes: prefilled.minutes }))
        }
        // Check if job has timesheets to allow mark-complete action
        try {
          const count = await getJobTimesheetCount(form.jobId)
          if (active) setCanMarkCompleted(Number(count?.count || 0) > 0)
        } catch {
          if (active) setCanMarkCompleted(false)
        }
      } catch {
        if (!active) return
        setSelectedJob(null)
        setCanMarkCompleted(false)
      }
    }
    loadSelectedJob()
    return () => {
      active = false
    }
  }, [form.jobId, isEditMode, mappedWorkName])

  const totalLoggedHours = useMemo(() => toHoursLogged(form.hours, form.minutes), [form.hours, form.minutes])

  const approvalLabel = useMemo(() => {
    if (form.approvalStatus === 'APPROVED') return 'Approved'
    if (form.approvalStatus === 'REJECTED') return 'Rejected'
    return 'Pending'
  }, [form.approvalStatus])

  const metadata = useMemo(() => {
    return {
      projectName: selectedJob?.project_name || '-',
      taskName: selectedJob?.task_name || '-',
      jobName: selectedJob?.job_name || mappedWorkName || '-',
      resourceInitials: String(resourceName || 'U')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase(),
      resourceName,
    }
  }, [mappedWorkName, resourceName, selectedJob])

  function patchForm(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function isCompletedJobSelected() {
    return !isViewMode && String(selectedJob?.status || '').trim().toUpperCase() === 'COMPLETED'
  }

  async function handleSave(options = {}) {
    if (isViewMode) return
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')
    if (!form.jobId) {
      setError('Job is required. Timesheet must be submitted against a job.')
      savingRef.current = false
      return
    }
    if (!isEditMode && !canAddTimesheet(selectedJob?.status)) {
      appToast.error('Job already completed. No further timesheet accepted.')
      savingRef.current = false
      return
    }
    if (totalLoggedHours <= 0) {
      setError('Hours logged must be greater than 0.')
      savingRef.current = false
      return
    }

    if (isCompletedJobSelected() && !options?.confirmed) {
      savingRef.current = false
      const confirmed = await appConfirm({
        title: 'Reopen Job to Pending Review?',
        message: `"${selectedJob?.job_name || mappedWorkName || 'This job'}" is currently completed. Saving changes to this timesheet will reopen the job to Pending Review for manager approval. Do you want to proceed?`,
        confirmText: 'Yes, Save & Reopen',
        cancelText: 'Cancel',
        variant: 'warning',
      })
      if (!confirmed) return
      return handleSave({ confirmed: true })
    }

    setIsSaving(true)
    try {
      const payload = {
        hoursLogged: totalLoggedHours,
        activityDescription: form.activityDescription || undefined,
        entryDate: form.entryDate || undefined,
      }
      let savedTimesheetId = null
      if (isEditMode) {
        await updateTimesheet(editingId, payload)
        setSuccess('Timesheet updated.')
        savedTimesheetId = editingId
      } else {
        const response = await createTimesheetFromJob(form.jobId, payload)
        setSuccess('Timesheet submitted.')
        savedTimesheetId = response?.timesheetId || response?.id
      }
      
      if (savedTimesheetId) {
        setTimesheetId(savedTimesheetId)
        try {
          const count = await getJobTimesheetCount(form.jobId)
          setCanMarkCompleted(Number(count?.count || 0) > 0)
        } catch {
          setCanMarkCompleted(true)
        }
      }
      
      window.setTimeout(() => {
        savingRef.current = false
        setIsSaving(false)
        navigate(returnUrl)
      }, 250)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to save timesheet.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  function handleDiscard() {
    navigate(returnUrl)
  }

  async function handleMarkJobDone(options = {}) {
    if (isViewMode) return
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')
    if (!form.jobId) {
      setError('Job is required. Timesheet must be submitted against a job.')
      savingRef.current = false
      return
    }
    if (totalLoggedHours <= 0) {
      setError('Hours logged must be greater than 0.')
      savingRef.current = false
      return
    }

    if (!options?.confirmed) {
      savingRef.current = false
      const confirmed = await appConfirm({
        title: 'Submit Job for Review?',
        message: `This will save your timesheet and submit "${selectedJob?.job_name || mappedWorkName || 'this job'}" for review. The job will remain in "Pending Review" until your timesheet is approved by your manager.`,
        confirmText: 'Submit for Review',
        cancelText: 'Cancel',
        variant: 'primary',
      })
      if (!confirmed) return
      return handleMarkJobDone({ confirmed: true })
    }

    setIsSaving(true)
    setIsMarkingComplete(true)
    try {
      const payload = {
        hoursLogged: totalLoggedHours,
        activityDescription: form.activityDescription || undefined,
        entryDate: form.entryDate || undefined,
      }
      if (isEditMode) {
        await updateTimesheet(editingId, payload)
      } else {
        await createTimesheetFromJob(form.jobId, payload)
      }

      // Mark the job status as PENDING_REVIEW with completion note
      const note = form.activityDescription || 'Submitted for review from timesheet entry.'
      try {
        await updateJobStatus(form.jobId, 'PENDING_REVIEW', note)
      } catch (jobErr) {
        console.warn('Job status update notice:', jobErr)
      }

      appToast.success('Timesheet saved and job submitted for review!')
      setSuccess('Timesheet saved and job submitted for review!')

      window.setTimeout(() => {
        savingRef.current = false
        setIsSaving(false)
        setIsMarkingComplete(false)
        navigate(returnUrl)
      }, 250)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to submit job for review.'))
      savingRef.current = false
      setIsSaving(false)
      setIsMarkingComplete(false)
    }
  }

  return {
    state: {
      form,
      isLoading,
      isSaving,
      isEditMode,
      isViewMode,
      error,
      success,
      mappedWorkName,
      selectedJob,
      totalLoggedHours,
      approvalLabel,
      metadata,
      canMarkCompleted,
      timesheetId,
      isMarkingComplete,
    },
    actions: {
      patchForm,
      fetchJobOptions,
      handleSave,
      handleDiscard,
      handleMarkJobDone,
      handleMarkJobCompleted: handleMarkJobDone,
    },
  }
}
